import refreshIcon from '../../../assets/images/icons/refresh-icon.svg'
import qrIcon from '../../../assets/images/icons/qr-icon.svg'
import saveIcon from '../../../assets/images/icons/download-icon.svg'
import infoIcon from '../../../assets/images/icons/info-outline.svg'
import checkIcon from '../../../assets/images/icons/checkGreen.svg'
import checkFilledIcon from '../../../assets/images/icons/checkFilledGreen.svg'
import errorIcon from '../../../assets/images/icons/errorOutline.svg'
import { PillButton } from '../../../components/buttons/PillButton'
import { Modal } from '../../../components/Modal'
import { useCallback, useContext, useRef, useState } from 'react'
import { SamplesContext, SamplesResponseType, UpdateSamplesContext } from '../../../context/samplesContext'
import { useNavigate } from 'react-router-dom'
import { buildPathWithSampleId, PATH_INFO, toSafeSampleIdPathSegment } from '../../utils/helper'
import { clientFetch } from '../../../utils/clientFetch'
import { useCanEdit } from '../../../hooks/useCanEdit'
import { MeasurementReportDownloadErrorModal } from '../utils/MeasurementReportDownloadErrorModal'
import { downloadMeasurementReport } from '../utils/measurementReportDownload'
import { useUserSizeCandidates } from '../../../hooks/useUserSizeCandidates'
import { useTableContext } from '../../../context/tableContext'
import { buildAnnotatedImages } from '../../Specification-download/helpers'
import { useTranslation } from 'react-i18next'

export function ButtonContainer({ isUpdate, setIsUpdate }: { isUpdate: boolean, setIsUpdate: React.Dispatch<React.SetStateAction<boolean>> }) {
  const { t } = useTranslation()
  const { canEdit, canOperate } = useCanEdit()
  const { sample, rowSample, canvasImage } = useContext(SamplesContext)
  const { saveSample, updateRowSample } = useContext(UpdateSamplesContext)
  const { getColor, getDisplayIndex } = useTableContext()
  const { refreshCandidates } = useUserSizeCandidates()
  const navigate = useNavigate()

  const [saveResult, setSaveResult] = useState<[boolean, number]>([false, 0])

  const [isSaveResultModalOpen, setIsSaveResultModalOpen] = useState(false)
  const closeSaveResultModal = useCallback(() => { setIsSaveResultModalOpen(false) }, [])
  const [isBlankSampleModal, setIsBlankSampleModal] = useState(false)
  const closeBlankSampleModal = useCallback(() => { setIsBlankSampleModal(false) }, [])
  const [downloadError, setDownloadError] = useState("")
  const [isDownloadErrorModalOpen, setIsDownloadErrorModalOpen] = useState(false)
  const closeDownloadErrorModal = useCallback(() => {
    setIsDownloadErrorModalOpen(false)
    setDownloadError("")
  }, [])

  const downloadPdf = useCallback(async () => {
    let sampleImages: string[] = []
    try {
      sampleImages = await buildAnnotatedImages(rowSample, getColor, getDisplayIndex)
    } catch (imageError) {
      console.error("採寸画像の描画に失敗しました。", imageError)
    }

    const errorMessage = await downloadMeasurementReport({
      sampleId: rowSample.id,
      sampleUrl: rowSample.url ?? undefined,
      sampleImage: canvasImage,
      sampleImages,
      navigate
    })
    if (errorMessage) {
      setIsSaveResultModalOpen(false)
      setDownloadError(errorMessage)
      setIsDownloadErrorModalOpen(true)
    }
  }, [navigate, rowSample, canvasImage, getColor, getDisplayIndex])

  const onSaveSample = useCallback(async () => {
    if (!canEdit) return
    const newSample = { ...rowSample }
    const newMeasurements = newSample.sampleMeasurements.filter((m) => !m.is_deleted)
    const hasBlankSample = newMeasurements.filter((m) => !m.measure_name)
    if (hasBlankSample.length > 0) {
      setIsBlankSampleModal(true)
      return
    }
    const result = await saveSample({ ...newSample, sampleMeasurements: newMeasurements }, isUpdate)
    setSaveResult(result)
    setIsSaveResultModalOpen(true)

    if (result[0]) {
      void refreshCandidates()
      await downloadPdf()
    }
  }, [saveSample, downloadPdf, rowSample, isUpdate, canEdit, refreshCandidates])

  return (
    <div className="w-full">
      <div className="flex flex-col space-y-[10px] mt-[5px] desktop-xmd:mt-[15px]">
        <div className='flex space-x-[5px] desktop-xmd:contents desktop-xmd:space-x-0 desktop-xmd:space-y-[10px]'>
          <PillButton
            label={t('actions.clear')}
            iconSrc={refreshIcon}
            toColor='#D9FFC7'
            fromColor='#278B25'
            onClickHandle={() => {
              updateRowSample(sample)
              setIsUpdate(false)
            }}
            canEdit={canEdit}
          />
          <PillButton
            label={t('actions.urlQrSearch')}
            iconSrc={qrIcon}
            toColor='#F9CEFF'
            fromColor='#A344BC'
            onClickHandle={() => {
              navigate(buildPathWithSampleId(PATH_INFO.QR_SEARCH.PATH, rowSample.id))
            }}
            canEdit={canOperate}
          />
        </div>
        <PillButton
          label={t('common.save')}
          iconSrc={saveIcon}
          toColor='#7FC1FF'
          fromColor='#2300FF'
          onClickHandle={onSaveSample}
          canEdit={canEdit}
        />
      </div>
      <SaveResultModal isOpen={isSaveResultModalOpen} onClose={closeSaveResultModal} saveResult={saveResult} sample={sample} />
      <BlankSampleModal isOpen={isBlankSampleModal} onClose={closeBlankSampleModal} />
      <MeasurementReportDownloadErrorModal
        isOpen={isDownloadErrorModalOpen && Boolean(downloadError)}
        onClose={closeDownloadErrorModal}
        message={downloadError}
      />
    </div>
  )
}

function SaveResultModal({ isOpen, onClose, saveResult, sample }: { isOpen: boolean, onClose: () => void, saveResult: [boolean, number], sample: SamplesResponseType }) {
  const { t } = useTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const openModal = useCallback(() => { setIsModalOpen(true) }, [])
  const closeModal = useCallback(() => { setIsModalOpen(false) }, [])
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  const onCopyUrl = useCallback(async () => {
    if (inputRef.current) {
      inputRef.current.select()
      try {
        await navigator.clipboard.writeText(inputRef.current.value)
      } catch (error) {
        console.error('リンクのコピーに失敗しました。')
      }
      openModal()
    }
  }, [openModal])

  const onQrDownload = useCallback(async (id: string) => {
    const safeSampleId = toSafeSampleIdPathSegment(id)
    if (!safeSampleId) {
      return
    }

    try {
      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/samples/${safeSampleId}/qr-code/download`, {
        method: "GET",
        headers: {
          'Content-Type': 'application/json',
        },
        handleUnauthorized: true
      })

      if (res.status === 401) {
        return
      }
      if (res.status === 403) {
        navigate(PATH_INFO.LOGIN.PATH)
        return
      }

      if (!res.ok) throw new Error('ダウンロード失敗')

      const blob = await res.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = `qr_${id}.png`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)

      window.URL.revokeObjectURL(downloadUrl)
    } catch (error) {
      console.log('DOWNLOAD ERROR')
    }
  }, [navigate])

  return (
    <>
      {saveResult[0] ? (
        <Modal isOpen={isOpen} onClose={onClose} className='flex flex-col items-center w-[485px] h-[745px] pt-5 px-5' >
          <div className='flex items-center justify-center w-full space-x-[10px] mb-[20px] mt-[50px]'>
            <img src={checkFilledIcon} alt='check' className='w-[26px] h-[26px]' />
            <span className='mt-[-5px]'>{t('messages.sampleSaveSucceeded')}</span>
          </div>
          <div className='flex items-center w-full'>
            <div className='w-2 h-[35px] rounded-sm bg-[#3E3E3E] mr-[5px]' ></div>
            <span className='text-[#3E3E3E] text-[16px]'>
              {t('sample.qrCode')}
            </span>
          </div>
          <div className='flex items-center justify-center w-[393px] h-[381px] border-[2.5px] border-[#D9D9D9] rounded-[20px] mt-[8px]'>
            <img src={sample.qr_code} alt='qrcode' className='w-[332px] h-[355px] rounded-[20px]' />
          </div>
          <div className='w-[445px] h-[40px] border-[2.5px] border-[#D9D9D9] rounded-[10px] mt-[8px]'>
            <input ref={inputRef} value={sample.url} className='w-full h-full px-[18px] rounded-[10px]' readOnly />
          </div>
          <div className='flex w-full justify-between items-center mt-[28px]'>
            <div className='relative group'>
              <span className={[
                'flex',
                'items-center',
                'justify-center',
                'w-[446px]',
                'h-[92px]',
                'px-[19px]',
                'absolute',
                'bg-white',
                'text-[14px]',
                '-translate-x-1/2',
                'left-1/2',
                'bottom-[-100px]',
                'opacity-0',
                'group-hover:opacity-100',
                'transition',
                "pointer-events-none"
              ].join(" ")} style={{ boxShadow: '0px 0px 4px 0px #00000040' }}>
                {t('messages.qrScanGuide')}
              </span>
              <img src={infoIcon} alt='info' className='w-6 h-6 cursor-help' />
            </div>
            <div className='space-x-[9px]'>
              <button
                className='w-[105px] h-[47px] border-[1.5px] border-[#C5C7FF] rounded-[50px] text-[#545899] text-[16px] font-semibold'
                onClick={onCopyUrl}>
                {t('common.copy')}
              </button>
              <button
                className='w-[160px] h-[47px] border-[1.5px] border-[#C5C7FF] rounded-[50px] text-[#545899] text-[16px] font-semibold'
                onClick={() => {
                  if (sample.id) {
                    onQrDownload(sample.id)
                  }
                }}
              >
                {t('common.download')}
              </button>
            </div>
          </div>
          <MessageModal isOpen={isModalOpen} onClose={closeModal} />
        </Modal>
      ) : (
        <Modal isOpen={isOpen} onClose={onClose} className='flex justify-center items-center w-[485px] h-[185px]' >
          <img src={errorIcon} alt='error' className='w-[26px] h-[26px] mr-[12px]' />
          <div className='flex flex-col'>
            {saveResult[1] === 409 ? (
              <>
                <span>{t('messages.duplicateSampleExists')}</span>
                <span>{t('messages.confirmInput')}</span>
              </>
            ) : (
              <>
                <span>{t('messages.sampleSaveFailed')}</span>
                <span>{t('messages.retrySaveButton')}</span>
              </>
            )}
          </div>
        </Modal>
      )
      }
    </>
  )
}

function MessageModal({ isOpen, onClose }: { isOpen: boolean, onClose: () => void }) {
  const { t } = useTranslation()

  return (
    <Modal isOpen={isOpen} onClose={onClose} className='flex items-center justify-center w-[414px] h-[130px]'>
      <img src={checkIcon} alt='check' className='w-6 h-6 mr-[5px]' />
      <span className='text-[14px]'>{t('messages.copyLinkSucceeded')}</span>
    </Modal>
  )
}

function BlankSampleModal({ isOpen, onClose }: { isOpen: boolean, onClose: () => void }) {
  const { t } = useTranslation()

  return (
    <Modal isOpen={isOpen} onClose={onClose} className='w-[500px] h-[200px] flex flex-col items-center justify-center'>
      <div className='flex items-center space-x-[5px]'>
        <img src={errorIcon} alt='error' className='w-[26px]' />
        <p>{t('messages.measurementNameMissing')}</p>
      </div>
      <button
        className='mt-[25px] w-[160px] h-[47px] border-[1.5px] border-[#C5C7FF] rounded-[50px] text-[#545899] font-bold'
        onClick={onClose}
      >
        {t('common.back')}
      </button>
    </Modal>
  )
}
